/**
 * Location Strategy - Queue-Driven Version
 * 
 * Algorithm:
 * 1. If no messages: Get completed queue items → runMatchesAnalyze → get top locations → runAIMatches → get ICP requirements → add to queue
 * 2. If messages exist: Use messages directly with runAIMatches → get ICP requirements → add to queue
 * 3. Use MULTI_ROW_SELECTOR template so existing queue system handles rendering
 * 
 * Integrated with DirectTopicCallback system
 */

import { runMatchesAnalyze, runAIMatches } from '../api';
import { StrategyPhase } from './baseStrategy';
import centralDiscoveryQueue from '../centralDiscoveryQueue';
import { 
  DISCOVERY_TEMPLATE_TYPES 
} from '../discoveryQueueSchema';

/**
 * Create location search messages for runAIMatches based on top locations
 * @param {Array} topLocations - Array of top location strings
 * @returns {Array} Messages array for runAIMatches
 */
const createLocationSearchMessages = (topLocations) => {
  const locationText = topLocations.join(' OR ');
  
  const messages = [
    {
      role: 'User', 
      content: `I want to find candidates in these locations: ${locationText}. Also include remote work options like "Remote - US Only" and "Remote - Global".`
    }
  ];
  
  return messages;
};

/**
 * Helper function to create queue item from requirement 
 * Follows the same pattern as skillsStrategy.jsx createQueueItemFromRequirement
 */
const createQueueItemFromRequirement = async (requirement) => {
  const { section_name } = requirement;
  
  if (!section_name) {
    console.log('⚠️ No section_name, skipping requirement');
    return null;
  }
  
  const topic = 'location';
  
  // Generate unique queue ID
  const queueId = `queue_${topic}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Parse the boolean string to extract location data and create user-friendly format
  let userFriendlyBoolStr = requirement.bool_str || '';
  let locationMapping = {};
  
  if (requirement.bool_str) {
    try {
      // Check if bool_str contains JSON array (location format)
      if (requirement.bool_str.startsWith('[{') && requirement.bool_str.endsWith('}]')) {
        const locationObjects = JSON.parse(requirement.bool_str);
        const locationNames = [];
        
        // Extract user-friendly names and create mapping
        locationObjects.forEach((locObj, index) => {
          const displayName = locObj.address || `Location ${index + 1}`;
          locationNames.push(displayName); // Don't add quotes - parseBooleanString doesn't expect them
          locationMapping[displayName] = locObj; // Store original object for reconstruction
        });
        
        // Create OR-ed boolean string with user-friendly names
        if (locationNames.length > 0) {
          userFriendlyBoolStr = `((${locationNames.join(' OR ')}))`;
        }
        
        console.log('🌍 LOCATION QUEUE: Converted JSON to user-friendly bool_str:', {
          original: requirement.bool_str.substring(0, 200) + '...',
          converted: userFriendlyBoolStr,
          locationCount: locationNames.length
        });
      }
    } catch (error) {
      console.error('🌍 LOCATION QUEUE: Error parsing location bool_str:', error);
      // Keep original bool_str if parsing fails
    }
  }

  // Create the flat queue item structure expected by the UI
  const queueItem = {
    // Core fields expected by UI
    queue_id: queueId,
    section_id: requirement.section_id || `${topic}_${Date.now()}`,
    section_name: requirement.section_name,
    section_type: requirement.section_type || 'Must Have',
    section_order: requirement.section_order || 200,
    bool_str: userFriendlyBoolStr,
    topic: topic,
    disco_done: false,
    
    // Store original location data for reconstruction
    location_mapping: locationMapping,
    original_bool_str: requirement.bool_str,
    
    // Additional metadata for template system
    template_type: DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR,
    ai_intro_message: `Based on what you're looking for, here are the top locations where similar candidates are most commonly found 🌍\nPick the location(s) you'd like to focus on, or feel free to make the role remote! 🗺️✨`,
    ux_config: {
      title: '', // Empty to avoid duplication with ai_intro_message
      confirm_button_text: 'Confirm Locations',
      allow_empty_selection: false,
      ro_footer: false // Enable footer so users can type alternative location requirements
    },
    
    // Timestamps
    created_at: Date.now(),
    updated_at: Date.now()
  };
  
  // The MULTI_ROW_SELECTOR template will parse the user-friendly bool_str and show location names
  
  return queueItem;
};

/**
 * Main location queue strategy function
 * @param {Object} decision - Current decision object from DirectTopicCallback
 * @param {Array} messages - Current conversation messages (empty when called from DirectTopicCallback)
 * @returns {Object} Updated decision object
 */
const locationQueueStrategy = async (decision, messages) => {
  console.log('🌍 LOCATION QUEUE STRATEGY - Processing location topic');
  console.log('🌍 LOCATION QUEUE STRATEGY - messages length:', messages ? messages.length : 0);
  console.log('🌍 LOCATION QUEUE STRATEGY - decision object:', {
    sessionId: decision.sessionId,
    currentSessionId: decision.currentSessionId,
    userId: decision.userId,
    getAllRequirements: !!decision.getAllRequirements,
    allDecisionProps: Object.keys(decision)
  });
  
  try {
    let locationAiMatchesResult = null;
    
    
    // Step 1: Determine approach based on whether messages exist
    if (messages && messages.length > 0) {
      console.log('🌍 LOCATION QUEUE STRATEGY: Using existing messages for runAIMatches');
      
      // Branch A: Use existing messages directly
      locationAiMatchesResult = await runAIMatches(messages);
      
    } else {
      console.log('🌍 LOCATION QUEUE STRATEGY: No messages, using queue items for location seeding');
      
      // Branch B: No messages - seed locations from completed queue items
      
      // Step 2: Get completed requirements from central discovery queue
      const allQueueItems = centralDiscoveryQueue.getAllItems();
      const completedItems = allQueueItems.filter(item => item.disco_done === true);
      
      console.log('🌍 LOCATION QUEUE STRATEGY: Found completed items:', completedItems.length);
      
      // Step 3: Convert completed queue items to ICP requirements format for runMatchesAnalyze
      let icpRequirements = [];
      
      if (completedItems.length > 0) {
        icpRequirements = completedItems.map(item => ({
          section_type: item.section_type || 'Must Have',
          section_name: item.section_name,
          section_id: item.section_id,
          bool_str: item.bool_str || ''
        }));
        
        console.log('🌍 LOCATION QUEUE STRATEGY: Constructed ICP requirements for analysis:', icpRequirements.length);
      } else {
        // Fallback: try to get requirements from decision.getAllRequirements
        if (decision.getAllRequirements) {
          const existingReqs = decision.getAllRequirements();
          if (existingReqs && existingReqs.length > 0) {
            icpRequirements = existingReqs;
            console.log('🌍 LOCATION QUEUE STRATEGY: Using existing requirements from decision:', icpRequirements.length);
          }
        }
      }
      
      // Step 4: Run matches analyze to get location data (if we have requirements)
      let locationKeys = [];
      
      if (icpRequirements.length > 0) {
        
        try {
          const matchesAnalyzeResult = await runMatchesAnalyze(icpRequirements, {
            run_on_self_org: true
          });
          
          console.log('🌍 LOCATION QUEUE STRATEGY: Matches analyze completed');
          
          
          // Step 5: Extract location data (following locationStrategy.jsx pattern)
          if (matchesAnalyzeResult?.analysis_info?.location_raw_map) {
            const locationMap = matchesAnalyzeResult.analysis_info.location_raw_map;
            
            // Sort location keys by the number of profiles (array length) in descending order
            locationKeys = Object.keys(locationMap)
              .sort((a, b) => {
                const aProfiles = Array.isArray(locationMap[a]) ? locationMap[a].length : 0;
                const bProfiles = Array.isArray(locationMap[b]) ? locationMap[b].length : 0;
                return bProfiles - aProfiles; // Sort in descending order
              });
              
            console.log('🌍 LOCATION QUEUE STRATEGY: Found locations from analysis:', {
              totalLocations: locationKeys.length,
              topLocations: locationKeys.slice(0, 5),
              totalProfiles: Object.values(locationMap).reduce((sum, profiles) => 
                sum + (Array.isArray(profiles) ? profiles.length : 0), 0
              )
            });
          }
        } catch (analyzeError) {
          console.error('🌍 LOCATION QUEUE STRATEGY: Matches analyze failed:', analyzeError);
          // Continue with fallback approach
        }
      }
      
      // Step 6: Create location search messages based on top locations or fallback
      let locationSearchMessages;
      
      if (locationKeys.length > 0) {
        // Use top 6 locations for the search
        const topLocations = locationKeys.slice(0, 6);
        locationSearchMessages = createLocationSearchMessages(topLocations);
        
        console.log('🌍 LOCATION QUEUE STRATEGY: Created location search with top locations:', topLocations);
      } else {
        // Fallback to common locations
        const fallbackLocations = ['San Francisco, CA', 'New York, NY', 'Los Angeles, CA', 'Seattle, WA', 'Austin, TX', 'Remote'];
        locationSearchMessages = createLocationSearchMessages(fallbackLocations);
        
        console.log('🌍 LOCATION QUEUE STRATEGY: Using fallback locations for search');
      }
      
      // Step 7: Run runAIMatches with location search messages
      
      locationAiMatchesResult = await runAIMatches(locationSearchMessages);
    }
    
    // Step 8: Extract and process location requirements from runAIMatches result
    if (!locationAiMatchesResult?.search_gen_response?.icp_requirements) {
      throw new Error('Failed to get location requirements from runAIMatches');
    }
    
    // Step 9: Filter for location requirements and add to central discovery queue
    const allRequirements = locationAiMatchesResult.search_gen_response.icp_requirements;
    const locationRequirements = allRequirements.filter(req => 
      req.section_name && (
        req.section_name.toLowerCase().includes('location') || 
        req.section_name.toLowerCase().includes('geography') ||
        req.section_name.toLowerCase().includes('remote')
      )
    );
    
    console.log('🌍 LOCATION QUEUE STRATEGY: Found location requirements from runAIMatches:', locationRequirements.length);
    
    
    const sessionId = decision.sessionId || decision.currentSessionId;
    if (sessionId && locationRequirements.length > 0) {
      await centralDiscoveryQueue.initialize(sessionId, []);
      
      // Check for existing location queue items to prevent duplicates
      const existingLocationItems = centralDiscoveryQueue.getItemsByTopic('location');
      console.log('🔍 LOCATION QUEUE STRATEGY: Found existing location queue items:', existingLocationItems.length);
      
      for (const requirement of locationRequirements) {
        // Check if queue item already exists for this section_name and topic
        const existingItem = existingLocationItems.find(item => 
          item.section_name === requirement.section_name && 
          item.section_type === requirement.section_type
        );
        
        if (existingItem) {
          console.log(`🔄 LOCATION QUEUE STRATEGY: Updating existing queue item for "${requirement.section_name}" (${requirement.section_type}) with latest data`);
          
          // Update existing item with latest data
          try {
            const updatedQueueItem = await createQueueItemFromRequirement(requirement, decision.queries || {});
            if (updatedQueueItem) {
              // Preserve original queue_id and timestamps, but update content
              updatedQueueItem.queue_id = existingItem.queue_id;
              updatedQueueItem.created_at = existingItem.created_at;
              updatedQueueItem.updated_at = Date.now();
              
              // Override the existing item in central queue
              centralDiscoveryQueue.queue.set(existingItem.queue_id, updatedQueueItem);
              centralDiscoveryQueue.notifyListeners();
              
              console.log('✅ Updated existing location queue item:', requirement.section_name);
            }
          } catch (error) {
            console.error('🌍 LOCATION QUEUE STRATEGY: Error updating queue item:', error);
          }
          continue;
        }
        
        try {
          const queueItem = await createQueueItemFromRequirement(requirement, decision.queries || {});
          if (queueItem) {
            await centralDiscoveryQueue.addQueueItem(queueItem);
            console.log('🌍 LOCATION QUEUE STRATEGY: Added location queue item:', requirement.section_name);
          }
        } catch (error) {
          console.error('🌍 LOCATION QUEUE STRATEGY: Error creating queue item:', error);
        }
      }
    } else if (!sessionId) {
      console.log('❌ LOCATION QUEUE STRATEGY: No session ID available, cannot add to queue');
    }
    
    // Step 10: Skip legacy requirements update to avoid duplicate location sections
    // The location data should be updated in matchResults instead of creating new ICP sections
    console.log('🌍 LOCATION QUEUE STRATEGY: Skipping legacy requirements update to prevent duplicate location sections');
    
    // Step 11: Notify completion callback if provided (for DirectTopicCallback integration)
    if (decision.onQueuePopulated) {
      decision.onQueuePopulated(locationRequirements.length);
    }
    
    console.log('🌍 LOCATION QUEUE STRATEGY: Enhanced central discovery queue populated with', centralDiscoveryQueue.getAllItems().length, 'total items');
    
    
    // Step 12: Return success - the queue system will handle rendering
    return {
      ...decision,
      actionType: 'CONTINUE_DISCOVERY', // DirectTopicCallback will handle this
      strategyPhase: StrategyPhase.CONVERSATION
    };
    
  } catch (error) {
    console.error('🌍 LOCATION QUEUE STRATEGY: Error in location strategy:', error);
    
    
    // Return default conversation flow - DirectTopicCallback will handle this
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: 'Let\'s discuss the location requirements for this role. What are your location preferences? Is remote work an option? Are you looking for candidates in specific regions or time zones?',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

export default locationQueueStrategy;